import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Receipt,
  Clock,
  MapPin,
  QrCode,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerBill() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('participant');
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBill();
  }, [sessionId, participantId]);

  const fetchBill = async () => {
    try {
      let url = `${API}/public/bill/${sessionId}`;
      if (participantId) {
        url += `?participant_id=${participantId}`;
      }
      
      const response = await axios.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching bill:', error);
      setError('No se pudo cargar la cuenta');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-brand mb-4" />
          <p className="text-gray-600">Cargando cuenta...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface p-4">
        <div className="text-center">
          <QrCode className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h1 className="text-xl font-bold mb-2">Cuenta no encontrada</h1>
          <p className="text-gray-600">{error || 'Esta sesión no existe o ya fue cerrada'}</p>
        </div>
      </div>
    );
  }

  const { business, unit, session, orders, participant } = data;

  // Calculate totals
  const subtotal = orders.reduce((sum, order) => sum + (order.subtotal || 0), 0);
  const tax = orders.reduce((sum, order) => sum + (order.tax || 0), 0);
  const serviceCharge = orders.reduce((sum, order) => sum + (order.service_charge || 0), 0);
  const total = subtotal + tax + serviceCharge;

  return (
    <div className="min-h-screen bg-surface p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <Card className="mb-4 bg-white">
          <CardContent className="p-6 text-center">
            <Receipt className="w-12 h-12 mx-auto text-brand mb-4" />
            <h1 className="font-heading text-2xl font-bold text-ink">{business.name}</h1>
            {business.address && (
              <p className="text-sm text-gray-600 mt-1 flex items-center justify-center gap-1">
                <MapPin className="w-4 h-4" />
                {business.address}
              </p>
            )}
            <div className="mt-4 flex items-center justify-center gap-4">
              <Badge variant="secondary" className="text-sm">
                {unit.name}
              </Badge>
              {participant && (
                <Badge className="bg-alert text-white text-sm">
                  {participant.name}
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-4 flex items-center justify-center gap-1">
              <Clock className="w-4 h-4" />
              {new Date(session.started_at).toLocaleString('es', {
                dateStyle: 'medium',
                timeStyle: 'short'
              })}
            </p>
          </CardContent>
        </Card>

        {/* Orders */}
        <Card className="mb-4 bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Detalle del Consumo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {orders.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No hay consumos registrados</p>
            ) : (
              orders.map((order) => (
                <div key={order.order_id} className="space-y-2">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between">
                      <div className="flex-1">
                        <span className="font-medium">
                          {item.quantity}x {item.product_name}
                        </span>
                        {item.variant && (
                          <span className="text-sm text-gray-500 ml-1">({item.variant})</span>
                        )}
                        {item.extras?.length > 0 && (
                          <p className="text-xs text-gray-500">+ {item.extras.join(', ')}</p>
                        )}
                      </div>
                      <span className="font-mono">${item.subtotal?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Totals */}
        <Card className="bg-white">
          <CardContent className="p-6 space-y-3">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span className="font-mono">${subtotal.toFixed(2)}</span>
            </div>
            
            {tax > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Impuestos</span>
                <span className="font-mono">${tax.toFixed(2)}</span>
              </div>
            )}
            
            {serviceCharge > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Servicio</span>
                <span className="font-mono">${serviceCharge.toFixed(2)}</span>
              </div>
            )}
            
            <Separator />
            
            <div className="flex justify-between text-xl font-bold">
              <span>Total</span>
              <span className="font-mono text-brand">${total.toFixed(2)}</span>
            </div>

            {session.status === 'closed' && (
              <div className="mt-4 p-3 bg-green-50 rounded-xl text-center">
                <Badge className="bg-green-500">Pagado</Badge>
                {session.tip > 0 && (
                  <p className="text-sm text-gray-600 mt-2">
                    Propina incluida: ${session.tip.toFixed(2)}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500">
          <p>¡Gracias por tu visita!</p>
          <p className="mt-1">{business.name}</p>
        </div>
      </div>
    </div>
  );
}
